import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import { CustomerStatusBadge } from '@/components/customer-portal/CustomerStatusBadge';
import { JOB_STATUS_TONE } from '../jobs/customer-job-status-tone';
import { waitingAge } from './customer-decision-queue';
import type { CustomerJobListItem } from '@/types/customer-job.types';

const URGENCY_CLASS: Record<string, string> = {
  urgent: 'border-l-customer-danger',
  attention: 'border-l-customer-warning',
  fresh: 'border-l-customer-accent',
};

const URGENCY_TEXT: Record<string, string> = {
  urgent: 'text-customer-danger-text',
  attention: 'text-customer-warning-text',
  fresh: 'text-customer-text-secondary',
};

/**
 * One pending decision. Always routes to ITS OWN record — `onOpen` is called
 * with this item's id and nothing else, so a queue of 100 rows produces 100
 * distinct destinations.
 *
 * Shows the waiting age derived from the server's `updatedAt`. The colour band
 * is emphasis only; the age is always stated in words too, so the urgency is
 * never carried by colour alone.
 */
export function CustomerDecisionCard({
  item,
  onOpen,
  emphasis = false,
}: {
  item: CustomerJobListItem;
  onOpen: (id: string) => void;
  /** The single most urgent item gets a heavier treatment on the dashboard. */
  emphasis?: boolean;
}) {
  const age = waitingAge(item.updatedAt);

  return (
    <button
      type="button"
      onClick={() => onOpen(item.id)}
      aria-label={`Open job ${item.inspectionNumber} — waiting ${age.label}`}
      className={[
        'customer-pressable flex w-full items-center gap-3 border-l-[3px] px-4 py-3.5 text-left transition-colors',
        'hover:bg-customer-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-customer-ring',
        URGENCY_CLASS[age.urgency] ?? URGENCY_CLASS.fresh,
        emphasis ? 'bg-customer-surface-muted/60' : '',
      ].join(' ')}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p
            className={`break-all text-sm font-semibold text-customer-text ${emphasis ? 'sm:text-base' : ''}`}
          >
            {item.inspectionNumber}
          </p>
          <CustomerStatusBadge
            label={item.customerFacingStatus}
            tone={JOB_STATUS_TONE[item.customerFacingStatus]}
          />
        </div>
        <p className="mt-1 break-words text-sm text-customer-text-secondary">
          {item.equipmentName}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-customer-text-secondary">
          <span className="break-all">Asset {item.equipmentAssetNumber}</span>
          <span aria-hidden="true">·</span>
          <span
            className={`inline-flex items-center gap-1 font-medium ${URGENCY_TEXT[age.urgency]}`}
          >
            <FontAwesomeIcon icon={faHourglassHalf} className="h-3 w-3" aria-hidden="true" />
            Waiting {age.label}
          </span>
        </p>
      </div>
      <FontAwesomeIcon
        icon={faChevronRight}
        className="h-3.5 w-3.5 shrink-0 text-customer-text-secondary"
        aria-hidden="true"
      />
    </button>
  );
}
